import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import api, { imageUrl } from "@/lib/api";
import { embedUrl, videoThumb } from "@/lib/video";
import { ArrowRight, Cpu, Cog, Wrench, Play, X } from "lucide-react";

const CAT_ORDER = ["assembly", "cutting", "soldering"];
const CAT_ICON = { assembly: Cpu, cutting: Cog, soldering: Wrench };

export default function Catalog() {
  const { t, machine, lang } = useLang();
  const [items, setItems] = useState([]);
  const [catVideos, setCatVideos] = useState({});
  const [videoModal, setVideoModal] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("cat") || "all";

  useEffect(() => {
    api.get("/catalog").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/catalog/category-videos").then(({ data }) => setCatVideos(data)).catch(() => {});
  }, []);

  const setActive = (cat) => {
    if (cat === "all") setSearchParams({});
    else setSearchParams({ cat });
  };

  const localized = (item) => {
    const tr = (item.translations && item.translations[lang]) || machine(item.model) || {};
    return { name: tr.name || item.name, description: tr.description || item.description };
  };

  const shownCats = active === "all" ? CAT_ORDER : CAT_ORDER.filter((c) => c === active);

  return (
    <div data-testid="catalog-page">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h1 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">{t("catalog.title")}</h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600">{t("catalog.subtitle")}</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="mb-10 flex flex-wrap gap-3" data-testid="catalog-filters">
          {["all", ...CAT_ORDER].map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`rounded-none border px-5 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                active === c ? "border-blue-700 bg-blue-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-blue-700 hover:text-blue-700"
              }`}
              data-testid={`filter-${c}`}
            >
              {c === "all" ? t("common.allCategories") : t(`categories.${c}`)}
            </button>
          ))}
        </div>

        {shownCats.map((cat) => {
          const catItems = items.filter((i) => i.category === cat);
          if (catItems.length === 0) return null;
          const Icon = CAT_ICON[cat];
          const vids = catVideos[cat] || [];
          return (
            <section key={cat} className="mb-16" data-testid={`catalog-category-${cat}`}>
              <div className="mb-6 flex items-center gap-3 border-b-2 border-slate-900 pb-3">
                <Icon className="h-6 w-6 text-blue-700" />
                <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t(`categories.${cat}`)}</h2>
                <span className="ml-auto font-mono text-sm text-slate-400">{catItems.length}</span>
              </div>
              {vids.length > 0 && (
                <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid={`catalog-category-videos-${cat}`}>
                  {vids.map((v, i) => (
                    <div key={`${cat}-v${i}`} className="aspect-video overflow-hidden border border-slate-200 bg-slate-900">
                      <iframe
                        src={embedUrl(v)}
                        title={`${cat} video ${i + 1}`}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {catItems.map((item) => {
                  const { name, description } = localized(item);
                  return (
                    <Link
                      key={item.id}
                      to={`/catalog/${item.id}`}
                      className="group flex flex-col border border-slate-200 bg-white transition-transform duration-200 hover:-translate-y-1 hover:border-blue-700"
                      data-testid={`catalog-item-${item.id}`}
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                        <img src={imageUrl(item.image_url)} alt={name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        {item.model && <span className="font-mono text-xs uppercase tracking-wider text-blue-700">{item.model}</span>}
                        <h3 className="mt-1 text-lg font-bold leading-snug tracking-tight text-slate-950">{name}</h3>
                        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">{description}</p>
                        {(item.video_urls || []).length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-2" data-testid={`catalog-video-thumbs-${item.id}`}>
                            {(item.video_urls || []).slice(0, 3).map((v, vi) => (
                              <button
                                key={`t${vi}`}
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVideoModal(embedUrl(v)); }}
                                className="group/vid relative aspect-video w-24 shrink-0 overflow-hidden border border-slate-200 bg-slate-900"
                                data-testid={`catalog-video-thumb-${item.id}-${vi}`}
                                aria-label="Play video"
                              >
                                {videoThumb(v) ? (
                                  <img src={videoThumb(v)} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <span className="absolute inset-0 bg-slate-800" />
                                )}
                                <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover/vid:bg-black/40">
                                  <Play className="h-5 w-5 fill-white text-white" />
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                        <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-bold uppercase tracking-wider text-blue-700">
                          {t("catalog.viewDetails")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          );
        })}
        {items.length === 0 && <p className="py-16 text-center text-slate-500">{t("common.loading")}</p>}
      </div>

      {videoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setVideoModal(null)}
          data-testid="catalog-video-modal"
        >
          <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setVideoModal(null)}
              className="absolute -top-10 right-0 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-white hover:text-blue-300"
              data-testid="catalog-video-modal-close"
            >
              <X className="h-5 w-5" /> {t("catalog.back")}
            </button>
            <div className="aspect-video w-full overflow-hidden bg-black">
              <iframe
                src={videoModal}
                title="Video"
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
